import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiCheckCircle, FiCalendar, FiClock, FiDownload, FiUpload, FiShield } from 'react-icons/fi';
import { Card, Button, AlertModal } from '../components/common';
import Navbar from '../components/Navbar';
import { formatDateTime } from '../data/carsData';
import { useAuth } from '../context/AuthContext';
import { getUserById, updateUserProfile } from '../firebase/users';
import { uploadUserDocument } from '../firebase/storage';

const Success = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: authUser, userProfile, refreshUserProfile } = useAuth();
  const [bookingData, setBookingData] = useState(null);
  const [bookingId, setBookingId] = useState('');
  const [user, setUser] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [alert, setAlert] = useState({ isOpen: false, type: 'success', title: '', message: '' });
  
  // Document refs
  const licenseFrontRef = useRef(null);
  const licenseBackRef = useRef(null);

  useEffect(() => {
    if (!location.state) {
      navigate('/');
      return;
    }
    setBookingData(location.state);
    setBookingId(location.state.bookingId || `BK${Date.now()}`);
    
    // Load user documents
    loadUserDocuments();
  }, [location, navigate, authUser, userProfile]);

  const loadUserDocuments = async () => {
    if (!authUser?.uid) return;
    
    try {
      // Use userProfile from context if available, otherwise fetch
      if (userProfile) {
        setUser(userProfile);
      } else {
        const result = await getUserById(authUser.uid);
        if (result.user) {
          setUser(result.user);
        }
      }
    } catch (error) {
      console.error('Error loading user documents:', error);
    }
  };

  // Handle document upload
  const handleDocumentUpload = async (documentType, file) => {
    if (!file) {
      console.error('No file provided to handleDocumentUpload');
      return;
    }

    const userId = user?.id || user?.uid || authUser?.uid;
    if (!userId) {
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'User not found. Please log in again.'
      });
      return;
    }

    setUploadingDoc(documentType);

    try {
      const result = await uploadUserDocument(file, userId, documentType);
      
      if (result.error) {
        setAlert({
          isOpen: true,
          type: 'error',
          title: 'Upload Failed',
          message: `Failed to upload ${documentType}: ${result.error}`
        });
        setUploadingDoc(null);
        return;
      }

      // Update user profile with document URL
      const updateData = {
        [`documents.${documentType}.url`]: result.url,
        [`documents.${documentType}.uploadedAt`]: new Date().toISOString(),
        [`documents.${documentType}.verified`]: false // Reset verification on new upload
      };

      const { error } = await updateUserProfile(userId, updateData);
      
      if (error) {
        setAlert({
          isOpen: true,
          type: 'error',
          title: 'Save Failed',
          message: `Failed to save document: ${error}`
        });
      } else {
        // Refresh user profile
        if (refreshUserProfile) {
          await refreshUserProfile();
        }
        const result = await getUserById(userId);
        if (result.user) {
          setUser(result.user);
        }
        setAlert({
          isOpen: true,
          type: 'success',
          title: 'Upload Successful',
          message: 'Document uploaded successfully! It will be verified by the manager.'
        });
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Upload Failed',
        message: 'Failed to upload document. Please try again.'
      });
    } finally {
      setUploadingDoc(null);
    }
  };

  // Handle file input change
  const handleFileChange = (documentType, e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setAlert({
        isOpen: true,
        type: 'warning',
        title: 'Invalid File Type',
        message: 'Please upload an image file (JPG, PNG, etc.)'
      });
      e.target.value = ''; // Reset input
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setAlert({
        isOpen: true,
        type: 'warning',
        title: 'File Too Large',
        message: 'File size must be less than 5MB. Please choose a smaller file.'
      });
      e.target.value = ''; // Reset input
      return;
    }
    
    handleDocumentUpload(documentType, file);
  };

  // Check if both license sides are uploaded
  const hasLicense = () => {
    return user?.documents?.['license-front']?.url && user?.documents?.['license-back']?.url;
  };

  const handleDownloadReceipt = () => {
    // Receipt download feature will be implemented soon
    // For now, this is a placeholder
  };

  if (!bookingData) {
    return null;
  }

  const { car, company, fromDateTime, toDateTime, duration, durationHours, costBreakdown, totalAmount, finalTotal: savedFinalTotal } = bookingData;

  const subtotal = totalAmount || 0;
  
  // Get pricing configuration from company (or use defaults if not available)
  const serviceChargePercent = company?.serviceChargePercent || 0;
  const serviceChargeEnabled = company?.serviceChargeEnabled !== false; // Default to true if not set
  const gstPercent = company?.gstPercent || 0;
  const gstEnabled = company?.gstEnabled !== false; // Default to true if not set
  
  // Calculate charges only if enabled
  const serviceCharge = serviceChargeEnabled && serviceChargePercent > 0 
    ? (subtotal * serviceChargePercent / 100) 
    : 0;
  const gst = gstEnabled && gstPercent > 0 
    ? (subtotal * gstPercent / 100) 
    : 0;
  
  const finalTotal = savedFinalTotal || (subtotal + serviceCharge + gst);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />

      <div className="py-6 sm:py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Success Header */}
          <Card className="p-6 sm:p-8 text-center mb-4 sm:mb-6 border-0 shadow-medium">
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-soft">
                <FiCheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Booking Sent to Manager</h1>
            <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">
              Your reservation request has been sent to the manager for approval. You will be notified once it's confirmed.
            </p>
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-primary-50 rounded-lg text-xs sm:text-sm">
              <span className="text-gray-600">Booking ID:</span>
              <span className="font-bold text-primary-600 font-mono">{bookingId}</span>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Left Column - Booking Details */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Car Info */}
            <Card className="p-4 sm:p-6 border-0 shadow-soft">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Booking Details</h2>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
                <img
                  src={car.image}
                  alt={car.name}
                  className="w-full sm:w-32 h-32 sm:h-24 object-contain bg-gray-100 rounded-xl"
                />
                <div className="flex-1">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">{car.name}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mb-2">{car.brand} • {car.model}</p>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-gray-200">
                <div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <FiCalendar className="w-4 h-4" />
                    <span>From</span>
                  </div>
                  <p className="font-semibold text-gray-900">{formatDateTime(fromDateTime)}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <FiCalendar className="w-4 h-4" />
                    <span>To</span>
                  </div>
                  <p className="font-semibold text-gray-900">{formatDateTime(toDateTime)}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <FiClock className="w-4 h-4" />
                    <span>Duration</span>
                  </div>
                  <p className="font-semibold text-gray-900">{duration}</p>
                  {costBreakdown && (
                    <p className="text-sm text-gray-600 mt-1">
                      {costBreakdown.slots24Hour > 0 && `${costBreakdown.slots24Hour} × 24hr`}
                      {costBreakdown.slots24Hour > 0 && costBreakdown.slots12Hour > 0 && ' + '}
                      {costBreakdown.slots12Hour > 0 && `${costBreakdown.slots12Hour} × 12hr`}
                    </p>
                  )}
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200 mt-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Amount</span>
                  <span className="text-2xl font-bold text-primary-600">
                    ₹{finalTotal.toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{duration}</p>
              </div>
            </Card>

            {/* License Upload Section */}
            <Card className="p-6 border-0 shadow-soft">
              <div className="mb-4">
                <h2 className="text-xl font-bold text-gray-900 mb-2">Driving License</h2>
                <p className="text-sm text-gray-600">
                  {hasLicense() 
                    ? 'Your driving license is uploaded. You can replace the images if needed.'
                    : 'Please upload both sides of your driving license to complete the booking process.'}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* License Front */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Front Side <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    {user?.documents?.['license-front']?.url ? (
                      <div className="relative">
                        <img
                          src={user.documents['license-front'].url}
                          alt="License Front"
                          className="w-full h-48 object-cover rounded-lg"
                        />
                        <div className="absolute top-2 right-2">
                          {user.documents['license-front'].verified ? (
                            <div className="bg-green-500 text-white rounded-full p-1.5">
                              <FiCheckCircle className="w-4 h-4" />
                            </div>
                          ) : (
                            <div className="bg-yellow-500 text-white rounded-full p-1.5">
                              <FiShield className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                        <input
                          ref={licenseFrontRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileChange('license-front', e)}
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => licenseFrontRef.current?.click()}
                          disabled={uploadingDoc === 'license-front'}
                          className="mt-2 w-full"
                        >
                          {uploadingDoc === 'license-front' ? (
                            <>Uploading...</>
                          ) : (
                            <>
                              <FiUpload className="mr-2" />
                              Replace
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <input
                          ref={licenseFrontRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileChange('license-front', e)}
                          className="hidden"
                        />
                        <FiUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-3">Upload license front</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => licenseFrontRef.current?.click()}
                          disabled={uploadingDoc === 'license-front'}
                        >
                          {uploadingDoc === 'license-front' ? 'Uploading...' : 'Choose File'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* License Back */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Back Side <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    {user?.documents?.['license-back']?.url ? (
                      <div className="relative">
                        <img
                          src={user.documents['license-back'].url}
                          alt="License Back"
                          className="w-full h-48 object-cover rounded-lg"
                        />
                        <div className="absolute top-2 right-2">
                          {user.documents['license-back'].verified ? (
                            <div className="bg-green-500 text-white rounded-full p-1.5">
                              <FiCheckCircle className="w-4 h-4" />
                            </div>
                          ) : (
                            <div className="bg-yellow-500 text-white rounded-full p-1.5">
                              <FiShield className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                        <input
                          ref={licenseBackRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileChange('license-back', e)}
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => licenseBackRef.current?.click()}
                          disabled={uploadingDoc === 'license-back'}
                          className="mt-2 w-full"
                        >
                          {uploadingDoc === 'license-back' ? (
                            <>Uploading...</>
                          ) : (
                            <>
                              <FiUpload className="mr-2" />
                              Replace
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <input
                          ref={licenseBackRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileChange('license-back', e)}
                          className="hidden"
                        />
                        <FiUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-3">Upload license back</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => licenseBackRef.current?.click()}
                          disabled={uploadingDoc === 'license-back'}
                        >
                          {uploadingDoc === 'license-back' ? 'Uploading...' : 'Choose File'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {hasLicense() && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> If you replace any image, it will need to be verified again by the manager.
                  </p>
                </div>
              )}
            </Card>
          </div>

            {/* Right Column - Actions */}
            <div className="lg:col-span-1">
            <Card className="p-4 sm:p-6 border-0 shadow-medium sticky top-4">
              <h3 className="font-semibold text-sm sm:text-base text-gray-900 mb-3 sm:mb-4">Next Steps</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 font-bold text-sm">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Upload License</p>
                    <p className="text-xs text-gray-500 mt-1">Upload both sides of your driving license</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    hasLicense() ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    <span className={`font-bold text-sm ${
                      hasLicense() ? 'text-green-600' : 'text-gray-400'
                    }`}>2</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Confirmation</p>
                    <p className="text-xs text-gray-500 mt-1">Receive booking confirmation</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-gray-400 font-bold text-sm">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Pickup</p>
                    <p className="text-xs text-gray-500 mt-1">Arrive on time with your documents</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={handleDownloadReceipt}
                >
                  <FiDownload className="mr-2" />
                  Download Receipt
                </Button>
                <Button
                  fullWidth
                  onClick={() => navigate('/')}
                  className="gradient-primary text-white"
                >
                  Book Another Car
                </Button>
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-sm text-blue-800">
                  <strong>Important:</strong> Please arrive on time for pickup. Bring your driving license and booking confirmation.
                </p>
              </div>
            </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        type={alert.type}
        title={alert.title}
        message={alert.message}
      />
    </div>
  );
};

export default Success;
